import { call, put, select, takeLatest } from "redux-saga/effects";

import type {
  ExecutiveSummarySchedule,
  ExecutiveSummaryScheduleCreatePayload,
  ExecutiveSummaryScheduleListResponse,
  ExecutiveSummaryScheduleUpdatePayload,
} from "@mapmycustomers/shared/types/entity/executiveSummarySchedule";
import type Organization from "@mapmycustomers/shared/types/Organization";

import {
  computeNextRunAt,
  DAY_OF_WEEK_TO_INT,
  detectBrowserTimezone,
  formatTimeOfDay,
  FREQUENCY_TO_BE,
} from "@app/scene/dashboard/components/ExecutiveSummaryContent/util/scheduleConversion";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam/selectors";

import {
  clearExecutiveSummarySchedule,
  fetchExecutiveSummarySchedule,
  saveExecutiveSummarySchedule,
} from "./actions";

function* onFetchExecutiveSummarySchedule({
  payload,
}: ReturnType<typeof fetchExecutiveSummarySchedule>) {
  try {
    const { scope } = payload;
    if (!scope.userId && !scope.teamId) {
      yield call(payload.callback, null);
      return;
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // The early return above guarantees one of userId/teamId is set; userId wins.
    const filter = scope.userId
      ? ({ scope: "user", userId: scope.userId } as const)
      : ({ scope: "team", teamId: scope.teamId! } as const);
    const response: ExecutiveSummaryScheduleListResponse = yield callApi(
      "fetchExecutiveSummarySchedules",
      orgId,
      filter
    );
    // At most one schedule per (scope, target); prefer an active row, fall back to any.
    const existing =
      response.data.find((row) => row.status === "active") ?? response.data[0] ?? null;
    yield call(payload.callback, existing);
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

function* onSaveExecutiveSummarySchedule({
  payload,
}: ReturnType<typeof saveExecutiveSummarySchedule>) {
  try {
    const { config, existingId, scope } = payload;
    if (!scope.userId && !scope.teamId) {
      throw new Error("scope.userId or scope.teamId is required");
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const timezone = detectBrowserTimezone();
    const frequency = FREQUENCY_TO_BE[config.frequency];
    const dayOfWeek = frequency === "WEEKLY" ? DAY_OF_WEEK_TO_INT[config.dayOfWeek] : null;
    const timeOfDay = formatTimeOfDay(config);
    const nextRunAt = computeNextRunAt(config, timezone);

    // (scope, target) are immutable on the BE, so an edit is an in-place PUT — no delete-then-create gap.
    if (existingId != null) {
      const updatePayload: ExecutiveSummaryScheduleUpdatePayload = {
        id: existingId,
        dayOfWeek,
        frequency,
        nextRunAt,
        timeOfDay,
        timezone,
      };
      const updated: ExecutiveSummarySchedule = yield callApi(
        "updateExecutiveSummarySchedule",
        orgId,
        existingId,
        updatePayload
      );
      yield call(payload.callback, updated);
      return;
    }
    const createPayload: ExecutiveSummaryScheduleCreatePayload = scope.userId
      ? {
          dayOfWeek,
          frequency,
          nextRunAt,
          scope: "user",
          timeOfDay,
          timezone,
          userId: scope.userId,
        }
      : {
          dayOfWeek,
          frequency,
          nextRunAt,
          scope: "team",
          teamId: scope.teamId,
          timeOfDay,
          timezone,
        };
    const saved: ExecutiveSummarySchedule = yield callApi(
      "createExecutiveSummarySchedule",
      orgId,
      createPayload
    );
    yield call(payload.callback, saved);
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

function* onClearExecutiveSummarySchedule({
  payload,
}: ReturnType<typeof clearExecutiveSummarySchedule>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteExecutiveSummarySchedule", orgId, payload.scheduleId);
    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error) {
    yield put(handleError({ error }));
    if (payload.failureCallback) {
      yield call(payload.failureCallback);
    }
  }
}

export function* executiveSummaryScheduleSagas() {
  yield takeLatest(fetchExecutiveSummarySchedule, onFetchExecutiveSummarySchedule);
  yield takeLatest(saveExecutiveSummarySchedule, onSaveExecutiveSummarySchedule);
  yield takeLatest(clearExecutiveSummarySchedule, onClearExecutiveSummarySchedule);
}
